import { AxiosPromise } from 'axios';
import Api, { baseURL } from '../../configs/AxiosConfig';
import {
  DeviceDetailsResponse,
  DeviceUpdateRequest,
  NewConnectionRequest,
  NewConnectionResponse,
  ResendNotificationRequest,
  ResendNotificationResponse,
  RestoreConnectionRequest,
  RestoreConnectionResponse,
  SuspendSimRequest,
  TerminateConnectionRequest,
  TerminateConnectionResponse,
  UpgradeDeviceRequest,
  UpgradeDeviceResponse,
  UserDevicesResponse,
  SimUpdateRequest,
  SimUpdateResponse,
  SchedulerContainsDeviceResponse,
  DeleteDeviceResponse,
  CancelScheduledOperationRequest,
  CancelScheduledOperationResponse,
  AssignDeviceRequest,
  AssignDeviceResponse,
  AddDeviceRequest,
  AddDeviceResponse,
  SimCreateRequest,
  SimCreateResponse,
} from '../../models/UserDevicesModels';
import {
  CreateUserRequest,
  CreateUserResponse,
  DeviceByEidRequest,
  DeviceByEidResponse,
  UserExistsRequest,
  UserExistsResponse,
  UserRequest,
  UserResponse,
  UsersListRequest,
  UsersListResponse,
  UpdateUserRequest,
  UpdateUserResponse,
  SchedulerContainsUserResponse,
  DeleteUserResponse,
  UserOperationsResponse,
  DeviceByNumberRequest,
} from '../../models/UsersModels';
import { FileFormat } from '../../models/ImportModels';
import { downloadFile } from '../../utils/files/downloadFile';

const UsersService = {
  api: {
    fetchUsers: (params: UsersListRequest): AxiosPromise<UsersListResponse> => Api.post(`/users/list`, params),
    fetchUser: (params: UserRequest): AxiosPromise<UserResponse> => Api.post(`/users/user`, params),
    userExists: (params: UserExistsRequest): AxiosPromise<UserExistsResponse> => Api.post(`/users/exists`, params),
    createUser: (params: CreateUserRequest): AxiosPromise<CreateUserResponse> => Api.post(`/users/create`, params),
    updateUser: (params: UpdateUserRequest): AxiosPromise<UpdateUserResponse> => Api.post(`/users/update`, params),
    fetchDeviceByEid: (params: DeviceByEidRequest): AxiosPromise<DeviceByEidResponse> =>
      Api.post(`/devices/eid`, params),
    fetchDeviceById: (id: string): AxiosPromise<DeviceDetailsResponse> => Api.get(`/devices/id/${id}`),
    updateDevice: (params: DeviceUpdateRequest): AxiosPromise<DeviceDetailsResponse> =>
      Api.post(`/devices/update`, params),
    fetchUserDevices: (
      id: string,
      params: { status: 'ALL' | 'FREE' | 'IN_USE'; deviceOnly: boolean },
    ): AxiosPromise<UserDevicesResponse> => Api.get(`/devices/user/${id}`, { params }),
    fetchFreeUserDevices: (id: string): AxiosPromise<UserDevicesResponse> => Api.get(`/devices/user/${id}/free`),
    restoreConnection: (params: RestoreConnectionRequest): AxiosPromise<RestoreConnectionResponse> =>
      Api.post(`/journey/restore`, params),
    suspendSim: (params: SuspendSimRequest): AxiosPromise<UpgradeDeviceResponse> =>
      Api.post(`/journey/suspend`, params),
    upgradeUserDevice: (params: UpgradeDeviceRequest): AxiosPromise<UpgradeDeviceResponse> =>
      Api.post(`/journey/update`, params),
    newConnection: (params: NewConnectionRequest): AxiosPromise<NewConnectionResponse> =>
      Api.post(`/journey/new`, params),
    resendNotification: (params: ResendNotificationRequest): AxiosPromise<ResendNotificationResponse> =>
      Api.post(`/journey/resend`, params),
    terminateConnection: (params: TerminateConnectionRequest): AxiosPromise<TerminateConnectionResponse> =>
      Api.post(`/journey/termination`, params),
    updateSim: (params: SimUpdateRequest): AxiosPromise<SimUpdateResponse> => Api.post(`/sim/update`, params),
    schedulerContainsDevice: (id: string): AxiosPromise<SchedulerContainsDeviceResponse> =>
      Api.get(`/scheduler/contains-device/${id}`),
    deleteDevice: (id: string): AxiosPromise<DeleteDeviceResponse> => Api.delete(`/devices/id/${id}`),
    cancelScheduledOperation: (
      params: CancelScheduledOperationRequest,
    ): AxiosPromise<CancelScheduledOperationResponse> => Api.post(`/journey/cancel`, params),
    schedulerContainsUser: (id: string): AxiosPromise<SchedulerContainsUserResponse> =>
      Api.get(`/scheduler/contains-user/${id}`),
    deleteUser: (id: string): AxiosPromise<DeleteUserResponse> => Api.delete(`/users/${id}`),
    fetchUserOperations: (params: UserRequest): AxiosPromise<UserOperationsResponse> =>
      Api.post('/users/user-operations', params),
    fetchDeviceByNumber: (params: DeviceByNumberRequest): AxiosPromise<DeviceDetailsResponse> =>
      Api.post(`/devices/number`, params),
    assignDevice: (params: AssignDeviceRequest): AxiosPromise<AssignDeviceResponse> =>
      Api.post(`/devices/assign`, params),
    addDevice: (params: AddDeviceRequest): AxiosPromise<AddDeviceResponse> => Api.post(`/devices/create`, params),
    createSim: (params: SimCreateRequest): AxiosPromise<SimCreateResponse> => Api.post(`/sim/create`, params),
    exportData: (params: UsersListRequest, format: FileFormat, fileName: string): Promise<void> => {
      // Build query string from params and format
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
      queryParams.append('format', format.toUpperCase());

      return downloadFile({
        url: `${baseURL}/users/report/export?${queryParams.toString()}`,
        filename: fileName,
      });
    },
  },
};

export default UsersService;
